import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CurrencyFindInterface } from '@zaoblako/shared/modules/catalog/dtos/currency/currency-find.dto';
import type { DirectionFindInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import { ChartModeEnum } from '@zaoblako/shared/modules/dashboard/enums/chart-mode.enum';
import { SalaryDistributionUserFormInterface } from '@zaoblako/shared/modules/document/dtos/salary-distribution/form/salary-distribution-user-form.dto';
import {
  SaleDistributionFormDto,
  SaleDistributionFormInterface
} from '@zaoblako/shared/modules/document/dtos/salary-distribution/form/sale-distribution-form.dto';
import { SalaryDistributionStatInterface } from '@zaoblako/shared/modules/document/dtos/salary-distribution/salary-distribution-stat.dto';
import { HelpdeskTaskReportMethodEnum } from '@zaoblako/shared/modules/helpdesk/enum/helpdesk.task.report.method.enum';
import { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import { CrmTableColumn } from '@zaoblako/web/core/models/crm-table-column.interface';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { DirectionService } from '@zaoblako/web/core/services/finance/direction.service';
import { SalaryDistributionService } from '@zaoblako/web/core/services/finance/salary-distribution.service';
import { GlobalService } from '@zaoblako/web/core/services/global.service';
import { UserService } from '@zaoblako/web/core/services/security/user.service';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { DatepickerComponent } from '@zaoblako/web/shared/dumbs/datepicker/datepicker.component';
import { DaterangepickerComponent } from '@zaoblako/web/shared/dumbs/daterangepicker/daterangepicker.component';
import { CurrencySingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/currency-single-select/currency-single-select.component';
import { DirectionSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/direction-single-select/direction-single-select.component';
import { DocumentTypeSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/document-type-single-select/document-type-single-select.component';
import { FilialSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/filial-single-select/filial-single-select.component';
import { OperationTypeSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/operation-type-single-select/operation-type-single-select.component';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { UnitSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/unit-single-select/unit-single-select.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { TableComponent } from '@zaoblako/web/shared/dumbs/table/table.component';
import { JoinWithChildPipe } from '@zaoblako/web/shared/pipes/join-with-child.pipe';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { flatten } from 'flat';
import { cloneDeep } from 'lodash';
import * as moment from 'moment-timezone';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { TableModule } from 'primeng/table';
import { forkJoin, Observable, of, switchMap, tap } from 'rxjs';
import type { CompanyFindInterface } from '@zaoblako/shared/modules/client/dtos/company/find/company-find.dto';
import { CompanyService } from '@zaoblako/web/core/services/client/company.service';
import { UserKindEnum } from '@zaoblako/shared/modules/security/enums/user.kind.enum';

@Component({
  standalone: true,
  selector: 'crm-document-salary-distribution-form',
  templateUrl: './document-salary-distribution-form.component.html',
  imports: [
    CommonModule,
    CardModule,
    DaterangepickerComponent,
    JoinPipe,
    ButtonModule,
    TableModule,
    TableComponent,
    SingleSelectComponent,
    DirectionSingleSelectComponent,
    DatepickerComponent,
    InputNumberModule,
    FormsModule,
    CheckboxModule,
    SpinnerComponent,
    JoinWithChildPipe,
    DialogModule,
    UnitSingleSelectComponent,
    OperationTypeSingleSelectComponent,
    FilialSingleSelectComponent,
    DocumentTypeSingleSelectComponent,
    CurrencySingleSelectComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DocumentSalaryDistributionFormComponent
  extends BaseFormComponent<SaleDistributionFormInterface>
  implements OnInit
{
  @Input() public readonly index: number;

  public distribution: SaleDistributionFormInterface = {
    from: moment().subtract(1, 'month').toDate(),
    to: moment().toDate(),
    settings: {
      direction: {
        target: null,
        distribute: false
      },
      unit: {
        target: null,
        distribute: false
      },
      filial: {
        target: null,
        distribute: false
      }
    },
    date: moment().toDate(),
    company: null,
    documentType: null,
    salaryDistributionUsers: []
  };

  public dto = SaleDistributionFormDto;
  public engine: 'class-transformer' | 'serde' = 'serde';
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public directions: DirectionFindInterface[] = [];
  public users: UserFindInterface[] = [];
  public companies: CompanyFindInterface[] = [];
  public currencies: CurrencyFindInterface[] = [];

  public sort = 'user.name:string';
  public isShowUserSelect = false;

  public userColumns: CrmTableColumn[] = [
    { label: 'Сотрудник', name: 'user', sort: 'user.name:string' },
    { label: 'Оклад', class: 'w-10rem' },
    { class: 'w-3rem' }
  ];

  public isShowUserWarning = false;
  public usersWithoutCounterparty: UserFindInterface[] = [];

  public userOptions: UserFindInterface[] = [];

  public isShowWarningDialog = false;
  public stat: SalaryDistributionStatInterface;

  public constructor(
    private readonly formStoreService: FormStoreService,
    public readonly globalService: GlobalService,
    private readonly salaryDistributionService: SalaryDistributionService,
    private readonly directionService: DirectionService,
    private readonly userService: UserService,
    private readonly companyService: CompanyService,
    private readonly errorService: ErrorService,
    private readonly router: Router,
    private readonly cdRef: ChangeDetectorRef
  ) {
    super();
  }

  public ngOnInit(): void {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    forkJoin([
      this.directionService.find<DirectionFindInterface>(),
      this.userService.findAll(),
      this.companyService.find<CompanyFindInterface>()
    ]).subscribe(([directions, users, companies]) => {
      this.directions = directions;
      this.companies = companies;
      this.users = users;
      this.changeShowUsersSelect();
      this.cdRef.markForCheck();
    });
  }

  public saveDistribution() {
    const requiredAnalyticalFeatures = this.globalService.globalInfo?.requiredAnalyticalFeatures;
    if (
      (requiredAnalyticalFeatures?.directionsRequired &&
        !(this.distribution.settings.direction?.distribute || this.distribution.settings.direction?.target)) ||
      (requiredAnalyticalFeatures?.unitsRequired &&
        !(this.distribution.settings.unit?.distribute || this.distribution.settings.unit?.target)) ||
      (requiredAnalyticalFeatures?.filialsRequired &&
        !(this.distribution.settings.filial?.distribute || this.distribution.settings.filial?.target))
    ) {
      this.errors = Object.create({});
      this.errors['settings'] = Object.create({});
      if (
        requiredAnalyticalFeatures?.directionsRequired &&
        !(this.distribution.settings.direction?.distribute || this.distribution.settings.direction?.target)
      ) {
        this.errors.settings.direction = 'Выберите или распределите бюджетную статью' as any;
      }
      if (
        requiredAnalyticalFeatures?.unitsRequired &&
        !(this.distribution.settings.unit?.distribute || this.distribution.settings.unit?.target)
      ) {
        this.errors.settings.unit = 'Выберите или распределите бизнес единицу' as any;
      }
      if (
        requiredAnalyticalFeatures?.filialsRequired &&
        !(this.distribution.settings.filial?.distribute || this.distribution.settings.filial?.target)
      ) {
        this.errors.settings.filial = 'Выберите или распределите филиал' as any;
      }
      return;
    }

    if (this.checkValidate(this.distribution).valid) {
      this.formStoreService.setLoading(true, this.index);

      this.userService
        .findAll()
        .pipe(
          tap((users) => {
            this.users = users;
            this.parseUsers();
            this.distribution.salaryDistributionUsers = this.distribution.salaryDistributionUsers.map(
              (distributionUser) => ({
                ...distributionUser,
                user: users.find((user) => user?.id === distributionUser.user?.id)
              })
            );
          }),
          switchMap(() => {
            this.usersWithoutCounterparty = this.distribution.salaryDistributionUsers
              .map(({ user }) => user)
              .filter((user) => !user.contragent);

            if (this.usersWithoutCounterparty?.length) {
              return of(null);
            }
            return this.salaryDistributionService.getStat(this.distribution);
          })
        )
        .subscribe({
          next: (stat) => {
            this.stat = stat;

            if (!stat) {
              this.isShowUserWarning = true;
              this.cdRef.markForCheck();
            } else if (stat?.total?.total) {
              this.isShowWarningDialog = true;
              this.cdRef.markForCheck();
            } else {
              this.createDistribution();
            }
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
    }
  }

  public addUser(user: UserFindInterface) {
    if (!Array.isArray(this.distribution.salaryDistributionUsers)) {
      this.distribution.salaryDistributionUsers = [];
    }
    this.distribution.salaryDistributionUsers.push({
      user,
      amount: 0
    });
    this.distribution.salaryDistributionUsers = [...this.distribution.salaryDistributionUsers];
    this.isShowUserSelect = false;
    this.cdRef.markForCheck();
  }

  public setDistributionPeriod(dates: [Date, Date]) {
    this.distribution.from = dates[0];
    this.distribution.to = dates[1];
  }

  public toggleAddUser() {
    this.isShowUserSelect = true;
    this.changeShowUsersSelect();
    this.cdRef.markForCheck();
  }

  public sortChange(sort: string) {
    this.sort = sort;
  }

  public toItem(item: any): SalaryDistributionUserFormInterface {
    return item as SalaryDistributionUserFormInterface;
  }

  public deleteUser(item: SalaryDistributionUserFormInterface) {
    this.distribution.salaryDistributionUsers = this.distribution.salaryDistributionUsers.filter(
      (_item) => _item.user?.id !== item.user?.id
    );
    this.changeShowUsersSelect();
  }

  public changeAmount(item: SalaryDistributionUserFormInterface, amount: number) {
    if (isNaN(amount) || !amount) {
      setTimeout(() => {
        item.amount = 0;
        this.cdRef.markForCheck();
      }, 10);
    }
    this.distribution.salaryDistributionUsers = [...this.distribution.salaryDistributionUsers];
  }

  public createDistribution() {
    this.salaryDistributionService.create<SaleDistributionFormInterface>(this.distribution).subscribe({
      next: (distribution) => {
        this.formStoreService.setLoading(false, this.index);
        this.errorService.addSuccessMessage('Распределение ЗП создано');
        this.formStoreService.saveNext(distribution, this.index);
      },
      error: () => {
        this.formStoreService.setLoading(false, this.index);
      }
    });
  }

  public redirectToTimeReport() {
    let queryParams;
    queryParams = {
      from: moment(this.distribution.from).format('YYYY-MM-DD') as unknown as Date,
      to: moment(this.distribution.to).format('YYYY-MM-DD') as unknown as Date,
      method: HelpdeskTaskReportMethodEnum.BY_TIME,
      groupSums: 'project',
      periodChart: ChartModeEnum.WEEK,
      isStartSearch: true
    };
    if (this.distribution.settings?.direction?.distribute && this.stat.total?.withoutDirections) {
      queryParams.withoutDirection = true;
    }
    if (this.distribution.settings?.unit?.distribute && this.stat.total?.withoutUnits) {
      queryParams.withoutUnit = true;
    }
    if (this.distribution.settings?.filial?.distribute && this.stat.total?.withoutFilials) {
      queryParams.withoutFilial = true;
    }
    if (this.distribution.salaryDistributionUsers?.length) {
      queryParams.userIds = this.distribution.salaryDistributionUsers?.map((user) => user.user?.id);
    }
    const url = String(this.router.createUrlTree(['/project/time-report'], { queryParams: flatten(queryParams) }));
    window.open(url, '_blank');
    this.isShowWarningDialog = false;
    this.formStoreService.setLoading(false, this.index);
    this.cdRef.markForCheck();
  }

  private changeShowUsersSelect() {
    if (
      !Array.isArray(this.distribution.salaryDistributionUsers) ||
      !this.distribution.salaryDistributionUsers?.length
    ) {
      this.userOptions = cloneDeep(this.users);
    } else {
      this.parseUsers();
    }
  }

  private parseUsers() {
    const parse = (users: UserFindInterface[]) => {
      [...users].forEach((user) => {
        if (this.distribution?.salaryDistributionUsers?.findIndex((item) => item.user?.id === user.id) !== -1) {
          users.splice(
            users.findIndex((_user) => _user.id === user.id),
            1
          );
        }
      });
    };

    this.userOptions = cloneDeep(this.users);
    parse(this.userOptions);
  }

  public redirectToUserList() {
    const queryParams = { kind: UserKindEnum.EMPLOYEE };
    const url = String(this.router.createUrlTree(['/user/list'], { queryParams: flatten(queryParams) }));
    window.open(url, '_blank');
    this.closeDialog();
  }

  public closeDialog() {
    this.isShowUserWarning = false;
    this.formStoreService.setLoading(false, this.index);
    this.cdRef.markForCheck();
  }

  public changeCompany() {
    this.currencies = [];
    this.distribution.currency = null;

    if (this.distribution.company?.currencies?.length) {
      this.currencies = this.distribution.company.currencies;
      setTimeout(() => {
        this.distribution.currency = this.currencies.find((currency) => currency.isDefault);
        this.cdRef.markForCheck();
      });
    }
  }
}
